// Core packages
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from "@angular/router";

// Third party packages

// Custom packages
import { isArchiveV3Live } from "src/app/shared/utils/archive-switch.utils";
import {
  ARCHIVE_V3_REDIRECT_DATA_KEY,
  archiveV3RedirectCommands,
} from "src/app/shared/utils/archive-v3-redirect.utils";

/**
 * The language of the address, the `:lang` parameter of the root route.
 *
 * A child route does not inherit the parameters of the routes above it (the router's default
 * `paramsInheritanceStrategy`), so it is looked for along the whole path, from the root down.
 *
 * @since 3.0.0
 *
 * @param route The route being activated
 * @returns The language, or null when no route of the path carries one
 */
export function archiveRouteLang(route: ActivatedRouteSnapshot | null | undefined): string | null {
  const path = route && Array.isArray(route.pathFromRoot) ? route.pathFromRoot : [];
  for (const step of path) {
    const lang = step && step.params ? step.params.lang : undefined;
    if (typeof lang === "string" && lang) {
      return lang;
    }
  }
  return null;
}

/**
 * Sends a legacy page of the archive on to its 3.0.0 successor once the archive is live (FE-A1),
 * @see ARCHIVE_V3_REDIRECT_DATA_KEY for the addresses and the rule.
 *
 * It goes FIRST in the `canActivate` of the legacy route, before the guards of that page:
 *
 *   {
 *     path: ":propertyId/documents",
 *     component: PropertyDocumentsComponent,
 *     canActivate: [ArchiveV3RedirectGuard, RoleGuard],
 *     data: { properties: [...], archiveV3Redirect: ARCHIVE_V3_PROPERTY_DOCUMENTS },
 *   }
 *
 * The router reads the guards of a route in their order and follows the first one that answers a
 * redirect, so the legacy page is never built while the switch is on; the guards of the new page then
 * decide whether the user may open it. While the switch is off it answers true and the legacy page
 * opens exactly as before, its own guards deciding.
 *
 * @since 3.0.0
 */
@Injectable({
  providedIn: "root",
})
export class ArchiveV3RedirectGuard implements CanActivate {
  /**
   * Class constructor
   */
  constructor(private router: Router) {}

  /**
   * The address of the new page while the archive is live and the route names one; true otherwise.
   *
   * @since 3.0.0
   *
   * @param route The legacy route being activated
   */
  canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
    const data = route && route.data ? route.data : {};
    const commands = archiveV3RedirectCommands(
      this.archiveLive(),
      data[ARCHIVE_V3_REDIRECT_DATA_KEY],
      route ? route.params : null,
      archiveRouteLang(route)
    );
    if (!commands) {
      return true;
    }
    return this.router.createUrlTree(commands, {
      queryParams: route.queryParams,
    });
  }

  /**
   * Whether the 3.0.0 archive is live in this build: a method of its own, so that a test can turn
   * the switch on without touching the environment files.
   *
   * @since 3.0.0
   */
  protected archiveLive(): boolean {
    return isArchiveV3Live();
  }
}
